/**
 * ============================================================================
 * DESIGN RULE - RECENT ACTIVITY TIMESTAMPS
 * ============================================================================
 * When an observation or issue is submitted via Quick Observation:
 * - The current date/timestamp is AUTOMATICALLY recorded
 * - The observation is added to Recent Activity with the timestamp
 * - Timestamp format: "MMM DD, YYYY" (e.g., "Jan 11, 2026")
 * - Items are displayed newest first (sorted by date descending)
 * - First sentence of observation is extracted as the title for searchability
 * ============================================================================
 */

import { useState } from "react";
import { Search, Volume2, X, ArrowLeft } from "lucide-react";
import { useRecentActivity, type Activity } from "@/hooks/useRecentActivity";

interface RecentActivityProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function RecentActivity({ isOpen = true, onClose }: RecentActivityProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  
  // Use shared activity hook
  const { activities, toggleSelection, selectAll, deselectAll } = useRecentActivity();
  
  // Report Generator fields
  const [reportDate, setReportDate] = useState(new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase());
  const [reportReference, setReportReference] = useState("");
  const [reportFrom, setReportFrom] = useState("Rank F Name MI L Name, Billet");
  const [reportTo, setReportTo] = useState("Operations Officer");
  const [reportVia, setReportVia] = useState("Rank Name, Billet");
  const [reportSubject, setReportSubject] = useState("");
  const [reportReferenceText, setReportReferenceText] = useState("");
  const [reportBody, setReportBody] = useState("");

  const selectedCount = activities.filter(a => a.selected).length;
  const allSelected = activities.length > 0 && activities.every(a => a.selected);

  // Filter activities to show only those from the last 120 days
  const filterByDate = (activity: Activity) => {
    const activityDate = new Date(activity.date);
    const today = new Date();
    const daysDifference = Math.floor((today.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysDifference <= 120;
  };

  const filteredActivities = activities
    .filter(filterByDate) // Only show activities from last 120 days
    .filter(activity =>
      activity.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.category.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Sort by date, newest first
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });

  const getSentimentEmoji = (sentiment: string) => {
    switch (sentiment) {
      case "Excited": return "😃";
      case "Happy": return "😊";
      case "Confused": return "😕";
      case "Concerned": return "😟";
      case "Angry": return "😠";
      case "Curious": return "🤔";
      default: return "😐";
    }
  };

  const handleGenerateReport = () => {
    // Generate report body from selected activities
    const selectedActivities = activities.filter(a => a.selected);
    const sustainItems = selectedActivities.filter(a => a.status === "SUSTAIN");
    const improveItems = selectedActivities.filter(a => a.status === "IMPROVE");
    
    let body = `1. Background.\\n\\nThis report summarizes ${selectedCount} observation${selectedCount !== 1 ? 's' : ''} recorded over the last 120 days. The observations are categorized into areas for improvement and practices to sustain.\\n\\n`;
    
    if (improveItems.length > 0) {
      body += `2. Areas for Improvement.\\n\\n`;
      improveItems.forEach((item, index) => {
        const letter = String.fromCharCode(97 + index); // a, b, c, etc.
        body += `   ${letter}. ${item.category}\\n\\n`;
        body += `      (1) Discussion. ${item.title}. ${item.description}\\n\\n`;
        body += `      (2) Recommendation. [Add specific recommendations here]\\n\\n`;
      });
    }
    
    if (sustainItems.length > 0) {
      body += `${improveItems.length > 0 ? '3' : '2'}. Practices to Sustain.\\n\\n`;
      sustainItems.forEach((item, index) => {
        const letter = String.fromCharCode(97 + index); // a, b, c, etc.
        body += `   ${letter}. ${item.category}\\n\\n`;
        body += `      (1) Discussion. ${item.title}. ${item.description}\\n\\n`;
      });
    }
    
    setReportBody(body);
    setShowReportGenerator(true);
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-gray-50 z-[80] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-[#1e3a8a] text-white px-6 py-8 relative">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 hover:bg-blue-800 rounded-full transition-colors"
          aria-label="Close Recent Activity"
        >
          <X className="size-6" />
        </button>
        
        <h1 className="text-2xl font-bold mb-2 text-gray-300">Recent Activity</h1>
        <p className="text-sm text-white">
          Select observations to generate comprehensive "After Action Reports" or memos. Newest items are displayed first.
        </p>
        
        {/* Search Bar */}
        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search observations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-white text-gray-900 placeholder:text-gray-500 border-2 border-black focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        
        {/* 120 Day Note */}
        <p className="text-xs text-white mt-2">
          Note: Recent 120-day activity is displayed in agent.
        </p>
      </div>

      {/* Selection Controls */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-4">
          <button 
            onClick={selectAll}
            className="text-gray-700 font-medium text-sm hover:underline hover:text-gray-900 transition-colors"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          
          <div className="text-xs text-blue-600 font-medium text-center leading-none">
            <div>{selectedCount} observation{selectedCount !== 1 ? 's' : ''}</div>
            <div className="text-blue-600">selected</div>
          </div>
          
          <button 
            onClick={deselectAll}
            className={`text-sm text-gray-700 hover:underline hover:text-gray-900 transition-colors ${selectedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={selectedCount === 0}
          >
            Clear selection
          </button>
          
          <button 
            className={`px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors ${selectedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleGenerateReport}
            disabled={selectedCount === 0}
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* Activity List - Scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 pb-32">
        {filteredActivities.map((activity) => (
          <div 
            key={activity.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 relative"
          >
            {/* Checkbox */}
            <div className="absolute top-4 right-4">
              <button
                onClick={() => toggleSelection(activity.id)}
                className="size-5 rounded border-2 border-gray-300 flex items-center justify-center bg-white hover:border-blue-600 transition-colors"
              >
                {activity.selected && (
                  <svg className="size-4 text-white" viewBox="0 0 24 24" fill="#2563eb">
                    <rect width="24" height="24" rx="2" />
                    <path 
                      d="M9 12l2 2 4-4" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Title */}
            <h3 className="font-semibold text-gray-900 pr-8 mb-2 text-[15px]">
              {activity.title}
            </h3>

            {/* Category Pill */}
            <div className="mb-3">
              <span className="px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 font-medium text-xs">
                {activity.category}
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              {activity.description}
            </p>

            {/* Metadata Row */}
            <div className="flex items-center gap-3 text-xs flex-wrap">
              {/* Date */}
              <div className="flex items-center gap-1.5 text-black">
                <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" fill="none"/>
                  <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2"/>
                  <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2"/>
                </svg>
                <span>{activity.date}</span>
              </div>

              {/* Status Badge */}
              <span className={`px-2.5 py-1 rounded-md font-semibold ${
                activity.status === "SUSTAIN" 
                  ? "bg-green-100 text-green-800" 
                  : "bg-orange-100 text-orange-800"
              }`}>
                {activity.status}
              </span>

              {/* Sentiment */}
              <div className="flex items-center gap-1.5">
                <span>{getSentimentEmoji(activity.sentiment)}</span>
                <span className="text-gray-700">{activity.sentiment}</span>
              </div>

              {/* Audio Icon */}
              <button 
                className="ml-auto p-1.5 hover:bg-gray-100 rounded transition-colors"
                aria-label="Play audio"
              >
                <Volume2 className="size-4 text-gray-500" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Report Generator Screen */}
      {showReportGenerator && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-black text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowReportGenerator(false)}
                className="hover:opacity-70 transition-opacity"
              >
                <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="font-semibold">Report Generator</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black rounded text-sm font-medium hover:bg-gray-100 transition-colors">
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors">
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
              <button 
                onClick={() => setShowReportGenerator(false)}
                className="hover:opacity-70 transition-opacity ml-2"
                aria-label="Close Report Generator"
              >
                <X className="size-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
            {/* Close Button */}
            <div className="flex justify-end">
              <button 
                onClick={() => setShowReportGenerator(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close Report Generator"
              >
                <svg className="size-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Report Header Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-black">
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <label className="font-semibold">Report Header (Editable)</label>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">Date</label>
                <input
                  type="text"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Reference Number */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">Reference Number (Optional)</label>
                <input
                  type="text"
                  value={reportReference}
                  onChange={(e) => setReportReference(e.target.value)}
                  placeholder="Reference Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* From */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">From</label>
                <input
                  type="text"
                  value={reportFrom}
                  onChange={(e) => setReportFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* To */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">To</label>
                <input
                  type="text"
                  value={reportTo}
                  onChange={(e) => setReportTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Via */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">Via (Optional)</label>
                <input
                  type="text"
                  value={reportVia}
                  onChange={(e) => setReportVia(e.target.value)}
                  placeholder="Rank Name, Billet"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">Subject</label>
                <input
                  type="text"
                  value={reportSubject}
                  onChange={(e) => setReportSubject(e.target.value)}
                  placeholder="AFTER ACTION REPORT FOR EVENT CONDUCTED FROM 29 JANUARY 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Reference */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-black">Reference (Optional)</label>
                <textarea
                  value={reportReferenceText}
                  onChange={(e) => setReportReferenceText(e.target.value)}
                  placeholder="(a) MCO 3504.1 Marine Corps Lessons Learned Program (MCLLP) and the Marine Corps Center for Lessons Learned (MCCLL)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
                  rows={3}
                />
              </div>
            </div>

            {/* Report Body Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-black">
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <label className="font-semibold">Report Body (Editable)</label>
              </div>

              {/* Body Text */}
              <div className="space-y-2">
                <textarea
                  value={reportBody}
                  onChange={(e) => setReportBody(e.target.value)}
                  placeholder="1. Background.&#10;&#10;This report summarizes observations recorded over time..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono leading-relaxed min-h-[400px]"
                  rows={20}
                />
                <button 
                  onClick={() => {
                    setShowReportGenerator(false);
                    setShowReportPreview(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview After Action Report Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Preview Screen */}
      {showReportPreview && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-black text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowReportPreview(false)}
                className="hover:bg-gray-800 rounded p-1 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="size-5" />
              </button>
              <h2 className="font-semibold">Preview</h2>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black rounded text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </button>
              <button 
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black rounded text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
              <button 
                onClick={() => setShowEmailPrompt(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium transition-colors"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </button>
              <button 
                onClick={() => setShowReportPreview(false)}
                className="hover:opacity-70 transition-opacity ml-1"
                aria-label="Close Preview"
              >
                <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Close Button */}
            <div className="flex justify-end mb-4">
              <button 
                onClick={() => setShowReportPreview(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close Preview"
              >
                <svg className="size-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-w-4xl mx-auto bg-gray-50 p-8 rounded-lg shadow-sm">
              <div className="font-mono text-sm text-black leading-relaxed space-y-4 whitespace-pre-wrap">
                <div className="text-center font-semibold">
                  DEPARTMENT OF THE NAVY
                  {"\n"}HEADQUARTERS UNITED STATES MARINE CORPS
                  {"\n"}TRAINING AND EDUCATION COMMAND
                </div>

                <div className="mt-6">
                  IN REPLY REFER TO:
                  {"\n"}{reportReference || "04JAN2026"}
                </div>

                <div className="mt-4">
                  FROM:  {reportFrom}
                  {"\n"}TO:    {reportTo}
                </div>

                {reportVia && (
                  <div>
                    VIA:   {reportVia}
                  </div>
                )}

                <div className="mt-4">
                  SUBJ:  {reportSubject || "AFTER ACTION REPORT FOR EVENT CONDUCTED FROM 03 MARCH 2024 TO 31 JANUARY 2026"}
                </div>

                {reportReferenceText && (
                  <div className="mt-4">
                    REF:   {reportReferenceText}
                  </div>
                )}

                <div className="mt-6">
                  {reportBody}
                </div>
              </div>
            </div>
          </div>

          {/* Email Prompt Modal */}
          {showEmailPrompt && (
            <div className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4" onClick={() => setShowEmailPrompt(false)}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-semibold text-lg mb-4">Email Report</h3>
                <p className="text-sm text-gray-600 mb-4">Enter your email address to receive a copy of this After Action Report.</p>
                
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowEmailPrompt(false);
                      setEmailAddress("");
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (emailAddress) {
                        // In a real implementation, this would send the email via an API
                        alert(`Report will be sent to: ${emailAddress}`);
                        setShowEmailPrompt(false);
                        setEmailAddress("");
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!emailAddress}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}